import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { AuthShell } from '@/components/auth/auth-shell';
import { PasswordStrengthMeter } from '@/components/auth/password-strength-meter';
import { ResetPasswordHero } from '@/components/auth/reset-password-hero';
import { Button } from '@/components/ui/button';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { resetPassword } from '@/services/api.client';

// Mirrors server PASSWORD_REGEX + MinLength(8) from password-reset.dto.ts
const STRONG_PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/;
const PASSWORD_RULES = 'At least 8 characters with uppercase, lowercase, and a digit';

const schema = z
  .object({
    newPassword: z.string().min(8, PASSWORD_RULES).regex(STRONG_PASSWORD_REGEX, PASSWORD_RULES),
    confirmPassword: z.string().min(1, 'Please confirm your new password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type FormValues = z.infer<typeof schema>;

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const mutation = useMutation({
    mutationFn: ({ newPassword }: FormValues) => {
      if (!token) throw new Error('Reset token is missing from the URL');
      return resetPassword(token, newPassword);
    },
    onSuccess: () => {
      void navigate('/login', { state: { passwordReset: true } });
    },
  });

  const onSubmit = (values: FormValues) => {
    mutation.mutate(values);
  };

  const watchedPassword = watch('newPassword', '');

  // Guard: token missing from URL — link from email is malformed
  if (!token) {
    return (
      <AuthShell
        hero={<ResetPasswordHero />}
        title="Invalid Reset Link"
        description="This reset link is missing required information. Please request a new one."
      >
        <Link
          to="/forgot-password"
          className="flex w-full justify-center text-sm underline underline-offset-4 hover:text-[var(--text-accent)]"
        >
          Request a new reset link
        </Link>
      </AuthShell>
    );
  }

  // 401 from server — token expired or already used. Inline banner replaces the
  // full-card pattern so the user sees the error in context of the form.
  const isTokenExpired =
    mutation.isError &&
    mutation.error instanceof Object &&
    'status' in mutation.error &&
    (mutation.error as { status: number }).status === 401;

  const apiError =
    mutation.isError &&
    !isTokenExpired &&
    mutation.error instanceof Object &&
    'message' in mutation.error
      ? String((mutation.error as { message: string }).message)
      : null;

  return (
    <AuthShell
      hero={<ResetPasswordHero />}
      title="Set New Password"
      description="Choose a strong password to protect your account."
    >
      <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} noValidate>
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="newPassword">New Password</FieldLabel>
            <div className="relative">
              <Input
                id="newPassword"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                disabled={mutation.isPending}
                aria-invalid={!!errors.newPassword}
                {...register('newPassword')}
              />
              <button
                type="button"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-foreground hover:text-foreground"
                onClick={() => setShowPassword((v) => !v)}
              >
                {showPassword ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
              </button>
            </div>
            {errors.newPassword ? (
              <p className="text-sm text-destructive" role="alert">
                {errors.newPassword.message}
              </p>
            ) : null}
            <PasswordStrengthMeter password={watchedPassword} />
          </Field>
          <Field>
            <FieldLabel htmlFor="confirmPassword">Confirm New Password</FieldLabel>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirm ? 'text' : 'password'}
                autoComplete="new-password"
                disabled={mutation.isPending}
                aria-invalid={!!errors.confirmPassword}
                {...register('confirmPassword')}
              />
              <button
                type="button"
                aria-label={showConfirm ? 'Hide password' : 'Show password'}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-foreground hover:text-foreground"
                onClick={() => setShowConfirm((v) => !v)}
              >
                {showConfirm ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-sm text-destructive" role="alert">
                {errors.confirmPassword.message}
              </p>
            )}
          </Field>

          {apiError && (
            <p className="text-sm text-destructive text-center" role="alert">
              {apiError}
            </p>
          )}

          <Field>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : 'Set New Password'}
            </Button>
          </Field>

          {/* Inline expired-link banner — shown after a 401 response */}
          {isTokenExpired && (
            <div
              role="alert"
              className="flex items-start gap-3 rounded-lg border p-3 text-sm"
              style={{
                borderColor: 'var(--status-red)',
                backgroundColor: 'var(--status-red-tint)',
                color: 'var(--text-error)',
              }}
            >
              This reset link has expired.{' '}
              <Link
                to="/forgot-password"
                className="ml-1 underline underline-offset-4 hover:text-[var(--text-accent)]"
              >
                Request a new one
              </Link>
              .
            </div>
          )}

          <p className="text-center text-sm text-[var(--text-secondary)]">
            <Link
              to="/login"
              className="underline underline-offset-4 hover:text-[var(--text-accent)]"
            >
              ← Back to Sign In
            </Link>
          </p>
        </FieldGroup>
      </form>
    </AuthShell>
  );
}
